import { useState } from "react";
import { NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import { LayoutDashboard, FolderOpen, ClipboardPlus, BarChart3, Users as UsersIcon, MapPin, ScrollText, HeartHandshake, BookOpen, KeyRound, LogOut, Menu, X, ShieldCheck, Trash2, Settings as SettingsIcon, CalendarDays, FileSearch } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ROLES } from "@/lib/constants";
import { LOGO } from "@/components/PublicLayout";

const NAV = [
  { to: "/office", label: "Dashboard", icon: LayoutDashboard, end: true, perm: ["cases:read_all", "cases:read_assigned"] },
  { to: "/office/cases", label: "Cases", icon: FolderOpen, perm: ["cases:read_all", "cases:read_assigned"] },
  { to: "/office/intake", label: "Office intake", icon: ClipboardPlus, perm: ["cases:intake"] },
  { to: "/office/visits", label: "Home visits", icon: CalendarDays },
  { to: "/office/survey", label: "Parish Survey", icon: FileSearch, perm: ["survey:manage"] },
  { to: "/office/reports", label: "Reports", icon: BarChart3, perm: ["reports:read"] },
  { to: "/office/volunteers", label: "Volunteers", icon: HeartHandshake, perm: ["volunteers:manage"] },
  { to: "/office/users", label: "Users & access", icon: UsersIcon, perm: ["users:manage"] },
  { to: "/office/gkks", label: "GKK directory", icon: MapPin, perm: ["gkk:manage"] },
  { to: "/office/audit", label: "Audit log", icon: ScrollText, perm: ["audit:read"] },
  { to: "/office/trash", label: "Deleted items", icon: Trash2, perm: ["users:manage", "gkk:manage", "volunteers:manage"] },
  { to: "/office/settings", label: "Settings", icon: SettingsIcon, perm: ["users:manage"] },
  { to: "/office/procedures", label: "Procedures", icon: BookOpen },
  { to: "/office/password", label: "Change password", icon: KeyRound },
];

function SideNav({ user, can, logout, onNav }) {
  const items = NAV.filter((n) => !n.perm || n.perm.some(can));
  return (
    <div className="flex flex-col h-full">
      <div className="px-5 py-6 flex items-center gap-3">
        <img src={LOGO} alt="Parish logo" className="w-11 h-11 object-contain bg-white rounded-full" />
        <div><p className="font-serif-head font-black tracking-wide leading-none">MABUHAY CARE</p><p className="text-xs text-[#E9DCCF] mt-1">Pastoral Office</p></div>
      </div>
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        {items.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} onClick={onNav} data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={({ isActive }) => `flex items-center gap-3 h-11 px-3 rounded-lg text-[15px] font-semibold transition-colors duration-150 ${isActive ? "bg-[#FDFBF7] text-[#4A2E1A]" : "text-[#F4EBE4] hover:bg-white/10"}`}>
            <n.icon className="w-[18px] h-[18px]" />{n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-white/10">
        <p data-testid="current-user-name" className="font-bold text-sm">{user.name}</p>
        <p data-testid="current-user-role" className="text-xs text-[#E9DCCF]">{ROLES[user.role]}</p>
        {user.safeguarding && <p className="mt-1 text-xs text-[#C59B27] flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Safeguarding authorized</p>}
        <button data-testid="logout-button" onClick={() => logout()} className="mt-3 h-10 w-full rounded-full border border-white/20 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-white/10"><LogOut className="w-4 h-4" /> Sign out</button>
      </div>
    </div>
  );
}

export default function StaffLayout() {
  const { user, can, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-[#605349]" data-testid="auth-loading">Loading…</div>;
  if (!user) return <Navigate to="/office/login" replace />;
  if (user.must_change_password && loc.pathname !== "/office/password") return <Navigate to="/office/password" replace />;
  return (
    <div className="min-h-screen bg-[#FDFBF7] lg:pl-64 print:pl-0 print:bg-white">
      <aside className="no-print hidden lg:block fixed inset-y-0 left-0 w-64 bg-[#4A2E1A] text-[#FDFBF7] z-30"><SideNav user={user} can={can} logout={logout} /></aside>
      <header className="no-print lg:hidden sticky top-0 z-30 h-16 bg-[#4A2E1A] text-white flex items-center justify-between px-4">
        <p className="font-serif-head font-black tracking-wide">MABUHAY CARE</p>
        <button data-testid="mobile-menu-button" aria-label="Menu" onClick={() => setOpen(true)} className="w-11 h-11 flex items-center justify-center"><Menu /></button>
      </header>
      {open && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/40" onClick={() => setOpen(false)}>
          <div className="absolute inset-y-0 left-0 w-72 bg-[#4A2E1A] text-white" onClick={(e) => e.stopPropagation()}>
            <button aria-label="Close menu" data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="absolute right-3 top-5 w-10 h-10 flex items-center justify-center"><X /></button>
            <SideNav user={user} can={can} logout={logout} onNav={() => setOpen(false)} />
          </div>
        </div>
      )}
      <main className="px-4 sm:px-8 py-8 max-w-7xl print:p-0 print:max-w-none"><Outlet /></main>
    </div>
  );
}
